/**
 * Language Registry — Single source of truth for all language knowledge in Vidi.
 *
 * To add a new language:
 * 1. Add a LanguageDefinition entry to LANGUAGE_REGISTRY
 * 2. Set canBeNative / canBeTarget as appropriate
 * 3. Define genders if the language has grammatical gender
 * 4. Add to VALID_LANG_CODES in edge functions (translate-grammar, suggest, etc.)
 *
 * Language pairs are auto-generated from all valid native×target combinations.
 */

// ─── Types ──────────────────────────────────────────────────────

export interface GrammaticalGender {
  /** The article or marker (e.g. "der", "el", "м") */
  article: string;
  /** Human-readable label (e.g. "masculine", "femenino") */
  label: string;
  /** Abstract role — source of truth for color, independent of the label's language. */
  role: 'masc' | 'fem' | 'neut';
  /** CSS text color */
  color: string;
  /** CSS background color (semi-transparent) */
  bgColor: string;
}

export interface LanguageDefinition {
  /** ISO language code, uppercase (e.g. "DE", "EN") */
  code: string;
  /** English name */
  nameEn: string;
  /** Native name */
  nameNative: string;
  /** Flag emoji */
  flag: string;
  /** BCP 47 locale for TTS (e.g. "de-DE") */
  ttsLocale: string;
  /** DeepL API language code (usually same as code) */
  deeplCode: string;
  /** Whether this language can be a native (base) language */
  canBeNative: boolean;
  /** Whether this language can be a target (learning) language */
  canBeTarget: boolean;
  /** UI locale for i18n (null = no translations available yet) */
  uiLocale: string | null;
  /** Grammatical gender definitions, or null if language has no gender */
  genders: Record<string, GrammaticalGender> | null;
  /** Regex pattern for script detection (e.g. Cyrillic, CJK) */
  scriptPattern?: RegExp;
  /** Whether phrases in this language have formal/informal register variants */
  hasFormalRegister?: boolean;
  /** Label for the formal register (e.g. "Sie", "usted", "vous") */
  formalLabel?: string;
  /** Label for the informal register (e.g. "du", "tú", "tu") */
  informalLabel?: string;
  /** Client-side detection hints for identifying this language from user input */
  detectionHints?: {
    specialChars?: RegExp;
    commonWords?: Set<string>;
    suffixPatterns?: RegExp;
    capitalizedNoun?: RegExp;
  };
}

export interface LanguagePair {
  /** Pair code: "{targetLower}-{nativeLower}" (e.g. "de-en") */
  code: string;
  /** Target language English name */
  target: string;
  /** Native language English name */
  native: string;
  /** Target language code */
  targetCode: string;
  /** Native language code */
  nativeCode: string;
  /** TTS locale for target language */
  ttsLocale: string;
  /** Whether target language has grammatical gender */
  hasGender: boolean;
  /** Whether target language has formal/informal register for phrases */
  hasFormalRegister: boolean;
  /** Labels for formal/informal variants (e.g. "Sie"/"du") */
  formalLabel?: string;
  informalLabel?: string;
}

export interface NativeLanguage {
  code: string;
  name: string;
  flag: string;
}

export interface TargetLanguage {
  code: string;
  name: string;
  flag: string;
}

// ─── Gender Colors (consistent across all languages) ────────────
// Masc / fem / neut = blue / red / green (German-learner convention).

/** Gender roles use CSS vars so Hearth/Dusk tokens apply without per-component hex. */
const MASCULINE: Pick<GrammaticalGender, 'role' | 'color' | 'bgColor'> = {
  role: 'masc',
  color: 'var(--gender-masc)',
  bgColor: 'color-mix(in srgb, var(--gender-masc) 14%, transparent)',
};

const FEMININE: Pick<GrammaticalGender, 'role' | 'color' | 'bgColor'> = {
  role: 'fem',
  color: 'var(--gender-fem)',
  bgColor: 'color-mix(in srgb, var(--gender-fem) 12%, transparent)',
};

const NEUTER: Pick<GrammaticalGender, 'role' | 'color' | 'bgColor'> = {
  role: 'neut',
  color: 'var(--gender-neut)',
  bgColor: 'color-mix(in srgb, var(--gender-neut) 14%, transparent)',
};

// ─── Language Registry ──────────────────────────────────────────

export const LANGUAGE_REGISTRY: LanguageDefinition[] = [
  {
    code: 'EN',
    nameEn: 'English',
    nameNative: 'English',
    flag: '🇬🇧',
    ttsLocale: 'en-US',
    deeplCode: 'EN',
    canBeNative: true,
    canBeTarget: true,
    uiLocale: 'en',
    genders: null,
    hasFormalRegister: false,
    detectionHints: {
      commonWords: new Set([
        'the',
        'a',
        'an',
        'is',
        'are',
        'am',
        'was',
        'were',
        'be',
        'been',
        'have',
        'has',
        'had',
        'do',
        'does',
        'did',
        'will',
        'would',
        'could',
        'should',
        'may',
        'might',
        'can',
        'shall',
        'must',
        'i',
        'you',
        'he',
        'she',
        'it',
        'we',
        'they',
        'my',
        'your',
        'his',
        'and',
        'or',
        'but',
        'not',
        'with',
        'for',
        'from',
        'at',
        'by',
        'to',
        'of',
        'in',
        'on',
        'if',
        'so',
        'than',
        'then',
        'that',
        'this',
        'what',
        'go',
        'come',
        'make',
        'take',
        'get',
        'give',
        'say',
        'tell',
        'know',
        'think',
        'see',
        'look',
        'find',
        'want',
        'need',
        'like',
        'love',
        'good',
        'bad',
        'big',
        'small',
        'new',
        'old',
        'long',
        'great',
        'hello',
        'thank',
        'thanks',
        'please',
        'sorry',
        'yes',
        'no',
        'time',
        'day',
        'year',
        'people',
        'way',
        'thing',
        'world',
        'house',
        'water',
        'food',
        'book',
        'school',
        'work',
        'life',
      ]),
      suffixPatterns: /(?:ing|tion|ment|ness|ful|less|able|ible|ous|ive|ly)$/i,
    },
  },
  {
    code: 'DE',
    nameEn: 'German',
    nameNative: 'Deutsch',
    flag: '🇩🇪',
    ttsLocale: 'de-DE',
    deeplCode: 'DE',
    canBeNative: true,
    canBeTarget: true,
    uiLocale: 'de',
    genders: {
      der: { article: 'der', label: 'masculine', ...MASCULINE },
      die: { article: 'die', label: 'feminine', ...FEMININE },
      das: { article: 'das', label: 'neuter', ...NEUTER },
    },
    hasFormalRegister: true,
    formalLabel: 'Sie (formal)',
    informalLabel: 'du (informal)',
    detectionHints: {
      specialChars: /[äöüßÄÖÜ]/,
      commonWords: new Set([
        'der',
        'die',
        'das',
        'ein',
        'eine',
        'einer',
        'eines',
        'einem',
        'einen',
        'ich',
        'du',
        'er',
        'sie',
        'es',
        'wir',
        'ihr',
        'mein',
        'dein',
        'sein',
        'und',
        'ist',
        'nicht',
        'haben',
        'werden',
        'können',
        'müssen',
        'wollen',
        'sollen',
        'dürfen',
        'machen',
        'gehen',
        'kommen',
        'sehen',
        'ja',
        'nein',
        'bitte',
        'danke',
        'gut',
        'schlecht',
        'haus',
        'hund',
        'katze',
        'buch',
        'schule',
        'arbeit',
        'freund',
        'frau',
        'mann',
        'kind',
        'zeit',
        'tag',
        'nacht',
        'wasser',
        'essen',
      ]),
      suffixPatterns: /(?:ung|keit|heit|schaft|lich|isch|chen|lein|tät|nis|ieren)$/i,
      capitalizedNoun: /^[A-ZÄÖÜ][a-zäöüß]{2,}$/,
    },
  },
  {
    code: 'ES',
    nameEn: 'Spanish',
    nameNative: 'Español',
    flag: '🇪🇸',
    ttsLocale: 'es-ES',
    deeplCode: 'ES',
    canBeNative: false,
    canBeTarget: true,
    uiLocale: 'es',
    genders: {
      el: { article: 'el', label: 'masculino', ...MASCULINE },
      la: { article: 'la', label: 'femenino', ...FEMININE },
    },
    hasFormalRegister: true,
    formalLabel: 'usted (formal)',
    informalLabel: 'tú (informal)',
    detectionHints: {
      specialChars: /[ñ¿¡áéíóú]/,
      commonWords: new Set([
        'el',
        'la',
        'los',
        'las',
        'un',
        'una',
        'es',
        'son',
        'está',
        'ser',
        'estar',
        'yo',
        'tú',
        'él',
        'ella',
        'nosotros',
        'ellos',
        'que',
        'de',
        'en',
        'por',
        'para',
        'con',
        'pero',
        'como',
        'más',
        'muy',
        'también',
        'aquí',
        'ahora',
        'hoy',
        'bueno',
        'hola',
        'gracias',
        'sí',
        'no',
        'bien',
        'mucho',
        'poco',
      ]),
      suffixPatterns: /(?:ción|dad|mente|ando|iendo|ado|ido|oso|osa)$/i,
    },
  },
  {
    code: 'FR',
    nameEn: 'French',
    nameNative: 'Français',
    flag: '🇫🇷',
    ttsLocale: 'fr-FR',
    deeplCode: 'FR',
    canBeNative: false,
    canBeTarget: false,
    uiLocale: 'fr',
    genders: {
      le: { article: 'le', label: 'masculin', ...MASCULINE },
      la: { article: 'la', label: 'féminin', ...FEMININE },
    },
    hasFormalRegister: true,
    formalLabel: 'vous (formal)',
    informalLabel: 'tu (informal)',
    detectionHints: {
      specialChars: /[àâçéèêëïîôùûüÿœæ]/,
      commonWords: new Set([
        'le',
        'la',
        'les',
        'un',
        'une',
        'des',
        'je',
        'tu',
        'il',
        'elle',
        'nous',
        'vous',
        'ils',
        'elles',
        'est',
        'sont',
        'avoir',
        'être',
        'que',
        'de',
        'en',
        'pour',
        'avec',
        'mais',
        'dans',
        'sur',
        'oui',
        'non',
        'merci',
        'bonjour',
        'bien',
        'très',
        'plus',
      ]),
      suffixPatterns: /(?:ment|tion|eur|euse|eux|aise|oire)$/i,
    },
  },
  {
    code: 'IT',
    nameEn: 'Italian',
    nameNative: 'Italiano',
    flag: '🇮🇹',
    ttsLocale: 'it-IT',
    deeplCode: 'IT',
    canBeNative: false,
    canBeTarget: false,
    uiLocale: 'it',
    genders: {
      il: { article: 'il', label: 'maschile', ...MASCULINE },
      la: { article: 'la', label: 'femminile', ...FEMININE },
      lo: { article: 'lo', label: 'neutro', ...NEUTER },
    },
    hasFormalRegister: true,
    formalLabel: 'Lei (formal)',
    informalLabel: 'tu (informal)',
    detectionHints: {
      specialChars: /[àèéìòù]/,
      commonWords: new Set([
        'il',
        'lo',
        'la',
        'gli',
        'le',
        'un',
        'una',
        'sono',
        'ho',
        'che',
        'non',
        'di',
        'in',
        'per',
        'con',
        'ma',
        'come',
        'anche',
        'sì',
        'no',
        'grazie',
        'buongiorno',
        'bene',
        'molto',
        'tutto',
      ]),
      suffixPatterns: /(?:zione|mente|ità|ando|endo|ato|uto|oso|osa)$/i,
    },
  },
  {
    code: 'PT',
    nameEn: 'Portuguese',
    nameNative: 'Português',
    flag: '🇵🇹',
    ttsLocale: 'pt-PT',
    deeplCode: 'PT-PT',
    canBeNative: false,
    canBeTarget: false,
    uiLocale: 'pt',
    genders: {
      o: { article: 'o', label: 'masculino', ...MASCULINE },
      a: { article: 'a', label: 'feminino', ...FEMININE },
    },
    hasFormalRegister: true,
    formalLabel: 'o senhor/a (formal)',
    informalLabel: 'tu/você (informal)',
    detectionHints: {
      specialChars: /[ãõçáéíóúâê]/,
      commonWords: new Set([
        'o',
        'a',
        'os',
        'as',
        'um',
        'uma',
        'é',
        'são',
        'ser',
        'ter',
        'eu',
        'tu',
        'ele',
        'ela',
        'nós',
        'eles',
        'elas',
        'que',
        'de',
        'em',
        'por',
        'para',
        'com',
        'mas',
        'como',
        'sim',
        'não',
        'obrigado',
        'olá',
        'bem',
        'muito',
        'também',
      ]),
      suffixPatterns: /(?:ção|mente|dade|ando|endo|ado|ido|oso|osa)$/i,
    },
  },
  {
    code: 'JA',
    nameEn: 'Japanese',
    nameNative: '日本語',
    flag: '🇯🇵',
    ttsLocale: 'ja-JP',
    deeplCode: 'JA',
    canBeNative: false,
    canBeTarget: false,
    uiLocale: 'ja',
    genders: null,
    scriptPattern: /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]/,
    hasFormalRegister: true,
    formalLabel: '丁寧語 (polite)',
    informalLabel: '普通体 (casual)',
  },
  {
    // Ukrainian MUST be before Russian — detection iterates in order,
    // and Ukrainian-specific chars (ґ, є, і, ї) must be checked before
    // the general Cyrillic range matches everything as Russian
    code: 'UK',
    nameEn: 'Ukrainian',
    nameNative: 'Українська',
    flag: '🇺🇦',
    ttsLocale: 'uk-UA',
    deeplCode: 'UK',
    canBeNative: false,
    canBeTarget: true,
    uiLocale: 'uk',
    genders: {
      ч: { article: 'ч', label: 'чоловічий', ...MASCULINE },
      ж: { article: 'ж', label: 'жіночий', ...FEMININE },
      с: { article: 'с', label: 'середній', ...NEUTER },
    },
    // Ukrainian-specific chars: ґ, є, і, ї
    scriptPattern: /[ґєіїҐЄІЇ]/,
    hasFormalRegister: true,
    formalLabel: 'Ви (formal)',
    informalLabel: 'ти (informal)',
  },
  {
    code: 'RU',
    nameEn: 'Russian',
    nameNative: 'Русский',
    flag: '🇷🇺',
    ttsLocale: 'ru-RU',
    deeplCode: 'RU',
    canBeNative: true,
    canBeTarget: false,
    uiLocale: 'ru',
    genders: {
      м: { article: 'м', label: 'мужской', ...MASCULINE },
      ж: { article: 'ж', label: 'женский', ...FEMININE },
      с: { article: 'с', label: 'средний', ...NEUTER },
    },
    // General Cyrillic — catches Russian and ambiguous Cyrillic text
    scriptPattern: /[\u0400-\u04FF]/,
    hasFormalRegister: true,
    formalLabel: 'Вы (formal)',
    informalLabel: 'ты (informal)',
  },
];

// ─── Lookup Helpers ─────────────────────────────────────────────

/** Get a language definition by code */
export function getLanguageDefinition(code: string): LanguageDefinition | undefined {
  return LANGUAGE_REGISTRY.find((l) => l.code === code);
}

// ─── Auto-Generated Pair Lists ──────────────────────────────────

/** All native languages (canBeNative === true) */
export const NATIVE_LANGUAGES: NativeLanguage[] = LANGUAGE_REGISTRY.filter(
  (l) => l.canBeNative,
).map((l) => ({ code: l.code, name: l.nameNative, flag: l.flag }));

/** All target languages (canBeTarget === true) */
export const TARGET_LANGUAGES: TargetLanguage[] = LANGUAGE_REGISTRY.filter(
  (l) => l.canBeTarget,
).map((l) => ({ code: l.code, name: l.nameEn, flag: l.flag }));

/** Generate all valid language pairs from registry */
function generateLanguagePairs(): LanguagePair[] {
  const pairs: LanguagePair[] = [];
  const natives = LANGUAGE_REGISTRY.filter((l) => l.canBeNative);
  const targets = LANGUAGE_REGISTRY.filter((l) => l.canBeTarget);

  for (const native of natives) {
    for (const target of targets) {
      // Skip same-language pairs
      if (native.code === target.code) continue;

      pairs.push({
        code: `${target.code.toLowerCase()}-${native.code.toLowerCase()}`,
        target: target.nameEn,
        native: native.nameEn,
        targetCode: target.code,
        nativeCode: native.code,
        ttsLocale: target.ttsLocale,
        hasGender: target.genders !== null,
        hasFormalRegister: target.hasFormalRegister ?? false,
        formalLabel: target.formalLabel,
        informalLabel: target.informalLabel,
      });
    }
  }

  return pairs;
}

export const SUPPORTED_LANGUAGE_PAIRS: LanguagePair[] = generateLanguagePairs();

// ─── Public API ─────────────────────────────────────────────────

export function getLanguagePair(code: string): LanguagePair | undefined {
  return SUPPORTED_LANGUAGE_PAIRS.find((p) => p.code === code);
}

export function getDefaultLanguagePair(): LanguagePair {
  return SUPPORTED_LANGUAGE_PAIRS.find((p) => p.code === 'de-en') ?? SUPPORTED_LANGUAGE_PAIRS[0];
}

/** Get target languages available for a given native language code */
export function getTargetLanguagesForNative(nativeCode: string): LanguagePair[] {
  return SUPPORTED_LANGUAGE_PAIRS.filter((p) => p.nativeCode === nativeCode);
}

/** Get emoji flag for a language code */
export function getLanguageFlag(code: string): string {
  return getLanguageDefinition(code)?.flag ?? '';
}

/** Get the i18n UI locale for a native language code (e.g. 'EN' → 'en', 'RU' → 'ru') */
export function getUiLocaleForNative(nativeCode: string): string {
  return getLanguageDefinition(nativeCode)?.uiLocale ?? 'en';
}

/** Extract the native language code from a language pair code (e.g. 'de-en' → 'EN') */
export function getNativeCodeFromPair(pairCode: string): string {
  const parts = pairCode.split('-');
  return (parts[1] ?? 'en').toUpperCase();
}

/**
 * Set of all grammatical articles across all languages (lowercase).
 * Used to distinguish "article + noun" (a word) from actual phrases.
 */
export const ALL_ARTICLES: Set<string> = new Set(
  LANGUAGE_REGISTRY.flatMap((lang) =>
    lang.genders ? Object.values(lang.genders).map((g) => g.article.toLowerCase()) : [],
  ),
);
